import { useState } from "react"
import { DateTime } from "luxon"
import { Box, Menu, MenuItem, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faComment, faPlus } from "@fortawesome/pro-light-svg-icons"
import { add, multiply, type Dinero } from "dinero.js"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../../currency/useMoney"
import { toDinero } from "../../../../../../currency/currencyLib"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { TranslationKey } from "../../../../../../localization/translations"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { FetchProductsForUserResponse, fetchProductsForUser, UserDetails } from "../../../../../../nonAdminUser/nonAdminUserApi"
import { MeetingServiceOrder } from "../../meetingServiceTypes"
import { ProductionOrderEdit, ProductionProductOption } from "../useProductionOrderEdit"
import { ProductionOrderLines } from "./ProductionOrderLines"
import { ProductionAddLinePicker, ProductionAddMode } from "./ProductionAddLinePicker"

const selectProductOptions = (data: FetchProductsForUserResponse): ProductionProductOption[] =>
    (data.productGroups ?? []).flatMap((group) =>
        (group.products ?? []).map((product) => ({
            productId: product.id,
            name: product.name,
            itemPrice: product.price,
        })),
    )

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
    },
    commentFrame: {
        display: "flex",
        alignItems: "center",
        padding: ".75rem 1rem",
        background: theme.palette.background.paper,
    },
    commentInner: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        padding: ".75rem 1rem",
        background: theme.palette.tailwind.sky[50],
        borderRadius: ".25rem",
    },
    commentIcon: {
        fontSize: "1rem",
        color: theme.palette.tailwind.sky[700],
    },
    commentText: {
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: ".009375rem",
        color: theme.palette.tailwind.sky[700],
    },
    editContainer: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
        padding: "1rem 1rem 1rem 7.5rem",
        background: theme.palette.tailwind.slate[50],
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("md")]: {
            paddingLeft: "1rem",
        },
    },
    editActions: {
        display: "grid",
        gridTemplateColumns: "9.875rem 1fr 4rem 7.5rem 2.5rem",
        columnGap: ".75rem",
        alignItems: "center",
    },
    editActionsLeft: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    addButton: {
        "&.MuiButton-root": {
            height: "3rem",
            padding: ".75rem 1rem",
            borderRadius: ".75rem",
            border: `1px solid ${theme.palette.tailwind.slate[300]}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.tailwind.slate[800],
            fontSize: "1.125rem",
            fontWeight: 500,
            letterSpacing: ".02875rem",
        },
        "& .MuiButton-startIcon svg": {
            fontSize: "1.125rem",
        },
    },
    totalValue: {
        gridColumn: "4 / 6",
        fontWeight: 700,
        fontSize: "1rem",
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
    },
}))

// `mode: null` appends a blank product row (product picked inline); a mode opens the picker panel.
const ADD_MENU_ITEMS: ReadonlyArray<{ id: TranslationKey; label: string; mode: ProductionAddMode | null }> = [
    { id: "meeting-service-production-view-add-product-button", label: "Add product", mode: null },
    // Hidden for now (Task 260) — reintroduce later with the same modal as Create order:
    // {
    //     id: "meeting-service-production-view-add-menu-extra-service",
    //     label: "Add extra service",
    //     mode: ProductionAddMode.EXTRA_SERVICE,
    // },
    // {
    //     id: "meeting-service-production-view-add-menu-custom-product",
    //     label: "Add custom product",
    //     mode: ProductionAddMode.WISH,
    // },
]

type Props = Readonly<{
    order: MeetingServiceOrder
    edit: ProductionOrderEdit
    onStartEdit: (deliveryKey: string, deliveryTime: DateTime) => void
}>

export function ProductionOrderItemsSection({ order, edit, onStartEdit }: Props) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null)
    const [addMode, setAddMode] = useState<ProductionAddMode | null>(null)

    // Shared product catalog for every editable line's product picker (React Query dedupes the request).
    const productsQuery = useApiQuery(fetchProductsForUser, {
        queryName: UserDetails.USER_PRODUCTS_LIST,
        dependencies: {
            userId: edit.addLookup?.userId ?? 0,
            merchantId: edit.addLookup?.merchantId ?? 0,
            priceListId: edit.addLookup?.priceListId ?? 0,
            deliveryDate: edit.addLookup?.deliveryDate ?? "",
        },
        enabled: edit.isEditing && Boolean(edit.addLookup),
        select: selectProductOptions,
    })

    const isEditing = edit.isEditing
    const hasNote = Boolean(order.note)

    const totalAmount = edit.editingLines.reduce<Dinero<number> | undefined>((total, line) => {
        const amount = multiply(toDinero(line.itemPrice), line.count)
        return total ? add(total, amount) : amount
    }, undefined)
    const totalLabel = totalAmount ? moneyFactory.format(totalAmount) : ""

    const handleAddSelect = (mode: ProductionAddMode | null) => {
        setAddMenuAnchor(null)
        if (mode === null) {
            edit.addBlankLine()
            return
        }
        setAddMode(mode)
    }

    const editFooter = (
        <Box className={classes.editContainer}>
            <Box className={classes.editActions}>
                <Box className={classes.editActionsLeft}>
                    <LocalizedStrict id="meeting-service-production-view-add-menu-button">
                        <StandardButton
                            fullWidth={false}
                            size="small"
                            variant="outlined"
                            className={classes.addButton}
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            disabled={!edit.addLookup}
                            onClick={(event) => setAddMenuAnchor(event.currentTarget)}
                        >
                            Add
                        </StandardButton>
                    </LocalizedStrict>
                </Box>

                {totalAmount && (
                    <LocalizedStrict id="meeting-service-production-view-total-with-amount" vars={{ amount: totalLabel }}>
                        <Typography variant="body1" className={classes.totalValue}>
                            Total: {totalLabel}
                        </Typography>
                    </LocalizedStrict>
                )}
            </Box>

            <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={() => setAddMenuAnchor(null)}>
                {ADD_MENU_ITEMS.map(({ id, label, mode }) => (
                    <LocalizedStrict key={id} id={id}>
                        <MenuItem onClick={() => handleAddSelect(mode)}>{label}</MenuItem>
                    </LocalizedStrict>
                ))}
            </Menu>

            {addMode && edit.addLookup && (
                <ProductionAddLinePicker
                    mode={addMode}
                    lookup={edit.addLookup}
                    onAddExtraService={edit.addExtraService}
                    onAddProductWish={edit.addProductWish}
                    onClose={() => setAddMode(null)}
                />
            )}
        </Box>
    )

    return (
        <Box className={classes.root}>
            <ProductionOrderLines
                order={order}
                groups={edit.deliveryGroups}
                meetingStart={order.meetingDetails?.startDateTime}
                meetingEnd={order.meetingDetails?.endDateTime}
                editingKey={edit.editingKey}
                isSaving={edit.isSaving}
                hasChanges={edit.hasChanges}
                onCountChange={edit.setLineCount}
                onDeliveryTimeChange={edit.setDeliveryTime}
                onRemove={edit.removeLine}
                onStartEdit={onStartEdit}
                onSave={edit.save}
                onCancel={edit.cancelEdit}
                productOptions={productsQuery.data ?? []}
                isProductsLoading={productsQuery.isInitialLoading}
                onProductChange={edit.setLineProduct}
                editFooter={editFooter}
            />

            {!isEditing && hasNote && (
                <Box className={classes.commentFrame}>
                    <Box className={classes.commentInner}>
                        <FontAwesomeIcon icon={faComment} className={classes.commentIcon} />
                        <Typography variant="body1" component="span" className={classes.commentText}>
                            {order.note}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    )
}
